import { defineMessages } from 'react-intl';

export default defineMessages({
  stateOpen: {
    id: 'frontbucket.pullRequestList.filter.open',
    description: 'Filter for showing open pull requests',
    defaultMessage: 'Open',
  },
  stateMerged: {
    id: 'frontbucket.pullRequestList.filter.merged',
    description: 'Filter for showing merged pull requests',
    defaultMessage: 'Merged',
  },
  stateDeclined: {
    id: 'frontbucket.pullRequestList.filter.declined',
    description: 'Filter for showing declined pull requests',
    defaultMessage: 'Declined',
  },
  stateDraft: {
    id: 'frontbucket.pullRequestList.filter.draft',
    description: 'Filter for showing draft pull requests',
    defaultMessage: 'Draft',
  },
  stateAll: {
    id: 'frontbucket.pullRequestList.filter.all',
    description: 'Filter for showing all pull requests',
    defaultMessage: 'All',
  },
  statusPlaceholder: {
    id: 'frontbucket.pullRequestList.filter.status.placeholder',
    description: 'Placeholder for the status filter',
    defaultMessage: 'Status',
  },
  userFilterReviewing: {
    id: 'frontbucket.pullRequestList.filter.pr.reviewing',
    description: 'Filter for showing pull requests that the user is reviewing',
    defaultMessage: "I'm reviewing",
  },
  userFilterWatching: {
    id: 'frontbucket.pullRequestList.filter.pr.watching',
    description: 'Filter for showing pull requests that the user is watching',
    defaultMessage: "I'm watching",
  },
  userFilterAll: {
    id: 'frontbucket.pullRequestList.filter.pr.all',
    description: 'Filter for showing all pull requests in the workspace',
    defaultMessage: 'All',
  },
  filterDescription: {
    id: 'frontbucket.pullRequestList.filter.search',
    description: 'Filter for showing pull requests that match the given text',
    defaultMessage: 'Search pull requests',
  },
  listHeader: {
    id: 'frontbucket.pullRequestList.list.header',
    description: 'Table header for the pull request list',
    defaultMessage: 'Summary',
  },
  pageTitle: {
    id: 'frontbucket.pullRequestList.page.title',
    description: 'Title for the pull request list page',
    defaultMessage: 'Pull requests',
  },
  buttonCreatePullRequest: {
    id: 'frontbucket.pullRequestList.action.createPullRequest',
    description: 'Action to create a new pull request',
    defaultMessage: 'Create pull request',
  },
  buttonCreatePullRequestDisabled: {
    id: 'frontbucket.pullRequestList.action.buttonCreatePullRequestDisabled',
    description:
      "Text for tooltip on the create pull request button that is disabled when the user doesn't have repository write priviliges.",
    defaultMessage: 'You do not have permission to create a pull request',
  },
  filterAuthor: {
    id: 'frontbucket.pullRequestList.filter.author',
    description:
      'Filter for showing the pull requests that match the selected author',
    defaultMessage: 'Author',
  },
  filterAuthorLoading: {
    id: 'frontbucket.pullRequestList.filter.author.loading',
    description:
      'Message shown in the author filter when it is loading the initial option if one is provided',
    defaultMessage: 'Loading...',
  },
  filterBranch: {
    id: 'frontbucket.pullRequestList.filter.branch',
    description:
      'Filter for showing the pull requests whose destination branch matches the selected branch',
    defaultMessage: 'Target branch',
  },
  filterBranchNoOptions: {
    id: 'frontbucket.pullRequestList.filter.branch.empty',
    description:
      'The message displayed in the branch filter when no branches are returned based on the search criteria',
    defaultMessage: 'No options',
  },
  filterBranchError: {
    id: 'frontbucket.pullRequestList.filter.branch.error',
    description:
      'The message displayed in the branch filter when the branches fail to load',
    defaultMessage: 'There was a problem fetching your branches',
  },
  filterAuthorNoOptions: {
    id: 'frontbucket.pullRequestList.filter.author.empty',
    description:
      'The message displayed in the author filter when no authors are returned based on the search criteria',
    defaultMessage: 'No options',
  },
  filterAuthorError: {
    id: 'frontbucket.pullRequestList.filter.author.error',
    description:
      'The message displayed in the author filter when the authors fail to load',
    defaultMessage:
      'We couldn’t get the list of authors. Try refreshing the page.',
  },
  authorPullRequests: {
    id: 'frontbucket.pullRequestList.filter.pr.author',
    description:
      'Filter for showing pull requests that the current user authored',
    defaultMessage: 'My pull requests',
  },
});

const noMatches = defineMessages({
  emptyResultsHeading: {
    id: 'frontbucket.pullRequestList.filter.emptyResults.no.matches.heading',
    description:
      'Text for the title of the message displayed when no pull requests are returned after a filter is applied',
    defaultMessage: 'No pull requests match',
  },
  emptyResultsInfo: {
    id: 'frontbucket.pullRequestList.filter.emptyResults.no.matches.info',
    description:
      'Text for the information in the message displayed when no pull requests are returned after a filter is applied',
    defaultMessage: 'Try modifying or clearing your filter criteria.',
  },
  clearAllFilters: {
    id: 'frontbucket.pullRequestList.filter.emptyResults.no.matches.clearAllFilters',
    description:
      'Text for the link to view open pull requests when no pull requests are returned after a filter is applied',
    defaultMessage: 'Clear all filters',
  },
  changeToAllFilter: {
    id: 'frontbucket.pullRequestList.filter.emptyResults.no.matches.changeToAllFilter',
    description:
      'Text for the link to view "all" pull requests when no pull requests are returned after a filter is applied',
    defaultMessage: 'Change filter to "All"',
  },
});

const noOpen = defineMessages({
  emptyResultsHeading: {
    id: 'frontbucket.pullRequestList.filter.emptyResults.no.open.heading',
    description:
      'Text for the title of the message displayed when no open pull requests exist',
    defaultMessage: 'No open pull requests',
  },
  emptyResultsInfo: {
    id: 'frontbucket.pullRequestList.filter.emptyResults.no.open.info',
    description:
      'Text for the information in the message displayed when no open pull requests exist',
    defaultMessage:
      "Things are pretty quiet right now, but when your team's coding, any open pull requests will appear here.",
  },
});

export const emptyResultsMessages = { noMatches, noOpen };

export const sortPrSelectorMessages = defineMessages({
  sortByLabel: {
    id: 'frontbucket.pullRequestList.filter.sortPrSelector.sort.by.label',
    description: 'Text for the <label> element above the select dropdown',
    defaultMessage: 'Sort by',
  },
  lastCreatedLabel: {
    id: 'frontbucket.pullRequestList.filter.sortPrSelector.last.created',
    description:
      'Text for the select dropdown text for sorting pull requests by last created',
    defaultMessage: 'Last created',
  },
  lastUpdatedLabel: {
    id: 'frontbucket.pullRequestList.filter.sortPrSelector.last.updated',
    description:
      'Text for the select dropdown text for sorting pull requests by last updated',
    defaultMessage: 'Last updated',
  },
});
